<script setup lang="ts">
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { DatePicker } from '@/components/ui/date-picker';
import { TableCell, TableRow } from '@/components/ui/table';
import TaskAssigneePicker from '@/components/tasks/TaskAssigneePicker.vue';
import TaskMilestoneSelect from '@/components/tasks/TaskMilestoneSelect.vue';
import TaskOptionMenu from '@/components/tasks/TaskOptionMenu.vue';
import { Trash2 } from 'lucide-vue-next';
import type { ProjectStage, TaskPriority } from '~/types/models';

const props = withDefaults(defineProps<{
    newTask: {
        title: string;
        status: string;
        assignee: string;
        assigneeId?: string;
        assigneeIds?: string[];
        assignees?: Array<{ id: string; label: string; avatarURL?: string }>;
        priority: TaskPriority | '';
        dueDate: string;
        clientVisible: boolean;
        projectId?: string;
        milestoneId?: string;
        clientId?: string;
    };
    statusOptions: Array<{ value: string; label: string; color?: string }>;
    priorityOptions?: Array<{ value: TaskPriority; label: string; color?: string }>;
    assigneeOptions: Array<{ id: string; label: string; avatarURL?: string }>;
    clientOptions: Array<{ value: string; label: string }>;
    projectOptions: Array<{ value: string; label: string }>;
    milestoneOptions?: ProjectStage[];
    showSelection?: boolean;
    showClientColumn?: boolean;
    showProjectColumn?: boolean;
}>(), {
    priorityOptions: () => [],
    milestoneOptions: () => [],
    showSelection: true,
    showClientColumn: true,
    showProjectColumn: true,
});

const emit = defineEmits<{
    (e: 'new-task-change', value: typeof props.newTask): void;
    (e: 'add-keydown', event: KeyboardEvent): void;
    (e: 'cancel-add'): void;
}>();

function updateField<K extends keyof typeof props.newTask>(field: K, value: typeof props.newTask[K]) {
    emit('new-task-change', { ...props.newTask, [field]: value });
}

function updateAssignees(assigneeIds: string[]) {
    const assignees = assigneeIds
        .map((id) => props.assigneeOptions.find((option) => option.id === id))
        .filter(Boolean) as Array<{ id: string; label: string; avatarURL?: string }>;

    emit('new-task-change', {
        ...props.newTask,
        assigneeIds,
        assignees,
        assigneeId: assigneeIds[0] || '',
        assignee: assignees[0]?.label || '',
    });
}

function updateProject(projectId: string) {
    emit('new-task-change', {
        ...props.newTask,
        projectId,
        milestoneId: '',
    });
}
</script>

<template>
    <TableRow class="new-task-row bg-muted/20 hover:bg-muted/25">
        <TableCell v-if="showSelection" class="w-[2.75rem] min-w-[2.75rem] max-w-[2.75rem]">
            <button
                type="button"
                class="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Cancel new task"
                title="Cancel new task"
                @click.stop="emit('cancel-add')"
            >
                <Trash2 class="size-3.5" />
            </button>
        </TableCell>
        <TableCell>
            <div class="space-y-2 py-1">
                <input
                    :value="newTask.title"
                    class="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Task name"
                    @input="updateField('title', ($event.target as HTMLInputElement).value)"
                    @keydown="emit('add-keydown', $event)"
                />
                <TaskMilestoneSelect
                    :model-value="String(newTask.milestoneId || '')"
                    :project-id="String(newTask.projectId || '')"
                    :milestones="milestoneOptions"
                    label=""
                    placeholder="No milestone"
                    storage-key="new-task-row-milestone"
                    trigger-class="h-8 bg-background text-xs"
                    @update:model-value="(value) => updateField('milestoneId', value)"
                />
            </div>
        </TableCell>
        <TableCell>
            <TaskOptionMenu
                :model-value="newTask.status || ''"
                type="status"
                :options="statusOptions"
                trigger-class="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-left text-sm"
                @update:model-value="(value) => updateField('status', value)"
            >
                <template #trigger="{ option }">
                    <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                        {{ option?.label || 'Select Status' }}
                    </span>
                </template>
            </TaskOptionMenu>
        </TableCell>
        <TableCell>
            <TaskOptionMenu
                :model-value="newTask.priority || ''"
                type="priority"
                :options="priorityOptions"
                allow-clear
                clear-label="No Priority"
                trigger-class="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-left text-sm"
                @update:model-value="(value) => updateField('priority', value as TaskPriority | '')"
            >
                <template #trigger="{ option }">
                    <span class="text-sm" :style="{ color: option?.color || '#94a3b8' }">
                        {{ option?.label || 'No Priority' }}
                    </span>
                </template>
            </TaskOptionMenu>
        </TableCell>
        <TableCell>
            <TaskAssigneePicker
                :model-value="newTask.assigneeIds || []"
                :options="assigneeOptions"
                trigger-class="h-9 w-full justify-between bg-background text-sm"
                @update:model-value="updateAssignees"
            />
        </TableCell>
        <TableCell v-if="showClientColumn">
            <SearchableSelectMenu
                :model-value="String(newTask.clientId || '')"
                :options="clientOptions"
                placeholder="Client"
                search-placeholder="Search clients..."
                allow-clear
                clear-label="No Client"
                storage-key="task-table-client-picker"
                empty-state-label="Search to find a client."
                trigger-class="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-left text-sm"
                @update:model-value="(value) => updateField('clientId', value)"
            >
                <template #trigger="{ option }">
                    <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                        {{ option?.label || 'No Client' }}
                    </span>
                </template>
            </SearchableSelectMenu>
        </TableCell>
        <TableCell v-if="showProjectColumn">
            <SearchableSelectMenu
                :model-value="String(newTask.projectId || '')"
                :options="projectOptions"
                placeholder="Project"
                search-placeholder="Search projects..."
                allow-clear
                clear-label="No Project"
                storage-key="task-table-project-picker"
                empty-state-label="Search to find a project."
                trigger-class="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-left text-sm"
                @update:model-value="updateProject"
            >
                <template #trigger="{ option }">
                    <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                        {{ option?.label || 'No Project' }}
                    </span>
                </template>
            </SearchableSelectMenu>
        </TableCell>
        <TableCell>
            <DatePicker
                :model-value="newTask.dueDate"
                :show-icon="false"
                placeholder="Select due date"
                @update:model-value="(value) => updateField('dueDate', value)"
            />
        </TableCell>
    </TableRow>
</template>
